import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Upload, CheckCircle, ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { MelissaColumnMapper, type ColumnMapping } from "./MelissaColumnMapper";

interface MelissaRecord {
  document_number: string;
  county: string;
  filing_date: string;
  first_name: string;
  last_name: string;
  seller_full_name: string;
  business_name: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  md_results: string;
  property_address: string;
  buyer: string;
  loan_amount: number | null;
  interest_rate: number | null;
  monthly_payment: number | null;
  term_months: number | null;
  balloon_amount: number | null;
  balloon_due_date: string;
  confidence_score: string;
  document_type: string;
  origin_state: string;
  phone_number: string;
  status: "good" | "review" | "bad";
  statusReason: string;
}

type Step = "upload" | "map" | "preview" | "done";

function classifyRecord(mdResults: string): { status: "good" | "review" | "bad"; reason: string } {
  if (!mdResults) return { status: "review", reason: "No MD results" };
  const codes = mdResults.split(",").map(c => c.trim());
  const hasAS01 = codes.includes("AS01");
  const hasAE02 = codes.includes("AE02");
  const hasAE10 = codes.includes("AE10");
  const hasAE01 = codes.includes("AE01");
  const hasAS12 = codes.includes("AS12");

  if (hasAE02 && !hasAS01) return { status: "bad", reason: "Unknown street" };
  if (hasAE10 && !hasAS01) return { status: "bad", reason: "Address not found" };
  if (hasAE01 && !hasAS01) return { status: "bad", reason: "General error" };
  if (hasAS12) return { status: "review", reason: "Address changed" };
  if (hasAS01) return { status: "good", reason: "Verified" };
  return { status: "review", reason: `Review: ${mdResults}` };
}

function parseNum(val: any): number | null {
  if (val == null || val === "") return null;
  const s = String(val).replace(/[$,\s]/g, "");
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

function parseDate(val: any): string | null {
  if (val == null || val === "") return null;

  const formatYmd = (y: number, m: number, d: number) =>
    `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const parseExcelSerial = (serial: number): string | null => {
    const parsed = XLSX.SSF.parse_date_code(serial);
    if (!parsed) return null;
    return formatYmd(parsed.y, parsed.m, parsed.d);
  };

  // Native Excel serial number
  if (typeof val === "number" && Number.isFinite(val)) {
    return parseExcelSerial(val);
  }

  const s = String(val).trim();
  if (!s) return null;

  // Excel serial stored as string (e.g. "44564")
  if (/^\d+(\.\d+)?$/.test(s)) {
    const serial = Number(s);
    if (Number.isFinite(serial) && serial > 0 && serial < 100000) {
      const parsed = parseExcelSerial(serial);
      if (parsed) return parsed;
    }
  }

  // MM/DD/YYYY or M/D/YYYY
  const usDate = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usDate) {
    const m = Number(usDate[1]);
    const d = Number(usDate[2]);
    const y = Number(usDate[3]);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) return formatYmd(y, m, d);
    return null;
  }

  // YYYY-MM-DD
  const isoDate = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoDate) {
    const y = Number(isoDate[1]);
    const m = Number(isoDate[2]);
    const d = Number(isoDate[3]);
    if (y >= 1900 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return formatYmd(y, m, d);
    }
    return null;
  }

  // Last fallback for parseable strings, but block impossible years
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) {
    const y = parsed.getUTCFullYear();
    if (y >= 1900 && y <= 2100) return parsed.toISOString().split("T")[0];
  }

  return null;
}

export function MelissaImport({ onImportComplete }: { onImportComplete?: () => void }) {
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [rawRows, setRawRows] = useState<any[]>([]);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [records, setRecords] = useState<MelissaRecord[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<{ updated: number; skipped: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback((f: File) => {
    setFile(f);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target?.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!raw.length) {
          toast.error("File has no data rows");
          return;
        }
        const headers = Object.keys(raw[0]);
        setRawRows(raw);
        setExcelHeaders(headers);
        setStep("map");
        toast.success(`Found ${raw.length} rows with ${headers.length} columns`);
      } catch (err) {
        toast.error("Failed to parse file");
        console.error(err);
      }
    };
    reader.readAsArrayBuffer(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleMappingConfirm = useCallback((mapping: ColumnMapping) => {
    const get = (row: any, dbField: string): string => {
      const col = mapping[dbField];
      if (!col) return "";
      return String(row[col] ?? "");
    };

    const sanitize = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, "");

    const parsed: MelissaRecord[] = rawRows.map((row, idx) => {
      const county = get(row, "county");
      const filingDate = get(row, "filing_date");
      const firstName = get(row, "first_name");
      const lastName = get(row, "last_name");
      const sellerFullName = get(row, "seller_full_name");
      const streetAddress = get(row, "street_address");
      const cityVal = get(row, "city");
      const stateVal = get(row, "state");
      const zipVal = get(row, "zip_code");
      const propertyAddress = get(row, "property_address");
      const explicitDocNumber = get(row, "document_number").trim();
      const mdResults = get(row, "md_results");
      const { status, reason } = classifyRecord(mdResults);

      const countyKey = sanitize(county) || "UNK";
      const nameKey = sanitize(`${lastName}${firstName}${sellerFullName}`).slice(0, 20) || "NONAME";
      const addressKey = sanitize(streetAddress || propertyAddress).slice(0, 24) || "NOADDR";
      const dateKey = sanitize(parseDate(filingDate) || filingDate).slice(0, 8) || "NODATE";
      const generatedId = `AUTOID-${countyKey}-${nameKey}-${addressKey}-${dateKey}-${String(idx + 1).padStart(4, "0")}`;

      return {
        document_number: explicitDocNumber || generatedId,
        county,
        filing_date: filingDate,
        first_name: firstName,
        last_name: lastName,
        seller_full_name: sellerFullName,
        street_address: streetAddress,
        city: cityVal,
        state: stateVal,
        zip_code: zipVal,
        md_results: mdResults,
        property_address: propertyAddress,
        business_name: get(row, "business_name"),
        phone_number: get(row, "phone_number"),
        buyer: get(row, "buyer"),
        loan_amount: parseNum(get(row, "loan_amount")),
        interest_rate: parseNum(get(row, "interest_rate")),
        monthly_payment: parseNum(get(row, "monthly_payment")),
        term_months: parseNum(get(row, "term_months")),
        balloon_amount: parseNum(get(row, "balloon_amount")),
        balloon_due_date: get(row, "balloon_due_date"),
        confidence_score: get(row, "confidence_score"),
        document_type: get(row, "document_type"),
        origin_state: get(row, "origin_state"),
        status,
        statusReason: reason,
      };
    });

    setRecords(parsed);
    const good = parsed.filter(r => r.status === "good").length;
    const review = parsed.filter(r => r.status === "review").length;
    const bad = parsed.filter(r => r.status === "bad").length;
    const autoIds = parsed.filter(r => r.document_number.startsWith("AUTOID-")).length;

    toast.success(`Parsed ${parsed.length} records: ${good} good, ${review} review, ${bad} bad`);
    if (autoIds > 0) {
      toast.warning(`${autoIds} rows missing document numbers — auto-generated IDs assigned.`);
    }
    setStep("preview");
  }, [rawRows]);

  const badRecords = records.filter(r => r.status === "bad");
  const importableRecords = records.filter(r => r.status === "good" || r.status === "review");
  const goodCount = records.filter(r => r.status === "good").length;
  const reviewCount = records.filter(r => r.status === "review").length;

  const handleImport = async () => {
    const toImport = importableRecords;
    if (!toImport.length) return;

    const deduped = new Map<string, MelissaRecord>();
    for (const r of toImport) {
      const key = (r.document_number || "").trim();
      if (!key) continue;
      if (!deduped.has(key)) deduped.set(key, r);
    }
    const uniqueRecords = Array.from(deduped.values());

    setImporting(true);
    setProgress(0);
    let updated = 0;
    const chunkSize = 100;

    try {
      for (let i = 0; i < uniqueRecords.length; i += chunkSize) {
        const chunk = uniqueRecords.slice(i, i + chunkSize);

        const insertRows = chunk.map(r => ({
            seller_id: r.document_number,
            county: r.county || null,
            first_name: r.first_name || null,
            last_name: r.last_name || null,
            business_name: r.business_name || null,
            phone_number: r.phone_number || null,
            street_address: r.street_address || null,
            city: r.city || null,
            state: r.state || null,
            zip_code: r.zip_code || null,
            property_address: r.property_address || null,
            consideration: r.loan_amount,
            recorded_date: parseDate(r.filing_date),
            document_type: r.document_type || null,
            origin_state: r.origin_state || null,
            mailing_status: "active",
        }));

        const { data, error } = await supabase
          .from("mailing_records")
          .upsert(insertRows, { onConflict: "seller_id" })
          .select("id");

        if (error) {
          console.error("Upsert error:", error);
          toast.error(`Error at batch ${Math.floor(i / chunkSize) + 1}: ${error.message}`);
          break;
        }

        updated += data?.length || 0;
        setProgress(Math.round(((i + chunkSize) / uniqueRecords.length) * 100));
      }

      setImportResult({ updated, skipped: badRecords.length });
      toast.success(`Import complete: ${updated} records imported`);
      setStep("done");
      onImportComplete?.();
    } catch (err) {
      console.error(err);
      toast.error("Import failed");
    } finally {
      setImporting(false);
      setProgress(100);
    }
  };

  const resetAll = () => {
    setStep("upload");
    setFile(null);
    setRawRows([]);
    setExcelHeaders([]);
    setRecords([]);
    setImportResult(null);
    setProgress(0);
  };

  const StatusIcon = ({ s }: { s: string }) => {
    if (s === "good") return <ShieldCheck className="h-3.5 w-3.5 text-green-600" />;
    if (s === "review") return <ShieldAlert className="h-3.5 w-3.5 text-yellow-600" />;
    return <ShieldX className="h-3.5 w-3.5 text-destructive" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Import Melissa Data Cleaned File
        </CardTitle>
        <CardDescription>
          {step === "upload" && "Upload your Melissa-cleaned Excel file to get started."}
          {step === "map" && "Step 1: Review and adjust column mappings before processing."}
          {step === "preview" && "Step 2: Review parsed records, then import."}
          {step === "done" && "Import complete."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* STEP 1: Upload */}
        {step === "upload" && (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = ".xlsx,.xls,.csv";
              input.onchange = (e: any) => { if (e.target.files[0]) handleFile(e.target.files[0]); };
              input.click();
            }}
          >
            <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm font-medium">Drop your Melissa-cleaned Excel file here</p>
            <p className="text-xs text-muted-foreground mt-1">Supports .xlsx, .xls, .csv</p>
          </div>
        )}

        {/* STEP 2: Column Mapping */}
        {step === "map" && (
          <MelissaColumnMapper
            excelHeaders={excelHeaders}
            sampleRows={rawRows.slice(0, 5)}
            onConfirm={handleMappingConfirm}
            onCancel={resetAll}
          />
        )}

        {/* STEP 3: Preview & Import */}
        {step === "preview" && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
                <ShieldCheck className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-lg font-bold">{goodCount}</p>
                  <p className="text-xs text-muted-foreground">Verified — will import</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
                <ShieldAlert className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="text-lg font-bold">{reviewCount}</p>
                  <p className="text-xs text-muted-foreground">Review — will also import</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
                <ShieldX className="h-5 w-5 text-destructive" />
                <div>
                  <p className="text-lg font-bold">{badRecords.length}</p>
                  <p className="text-xs text-muted-foreground">Bad — skipping</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{file?.name} — {records.length} records</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setStep("map")}>Back to Mapping</Button>
                <Button variant="outline" size="sm" onClick={resetAll}>Clear</Button>
                <Button size="sm" onClick={handleImport} disabled={importing || importableRecords.length === 0}>
                  {importing ? "Importing..." : `Import ${importableRecords.length} Records (${goodCount} verified + ${reviewCount} review)`}
                </Button>
              </div>
            </div>

            {importing && <Progress value={progress} className="h-2" />}

            <div className="border rounded-md overflow-auto max-h-[600px]">
              <Table>
                <TableHeader>
                  <TableRow className="text-xs">
                    <TableHead className="w-8 sticky left-0 bg-background z-10">✓</TableHead>
                    <TableHead className="whitespace-nowrap">Doc #</TableHead>
                    <TableHead className="whitespace-nowrap">County</TableHead>
                    <TableHead className="whitespace-nowrap">Filing Date</TableHead>
                    <TableHead className="whitespace-nowrap">Doc Type</TableHead>
                    <TableHead className="whitespace-nowrap">Origin St</TableHead>
                    <TableHead className="whitespace-nowrap">First Name</TableHead>
                    <TableHead className="whitespace-nowrap">Last Name</TableHead>
                    <TableHead className="whitespace-nowrap">Street Address</TableHead>
                    <TableHead className="whitespace-nowrap">City</TableHead>
                    <TableHead className="whitespace-nowrap">State</TableHead>
                    <TableHead className="whitespace-nowrap">Zip</TableHead>
                    <TableHead className="whitespace-nowrap">Buyer</TableHead>
                    <TableHead className="whitespace-nowrap">Property Address</TableHead>
                    <TableHead className="whitespace-nowrap">Loan Amt</TableHead>
                    <TableHead className="whitespace-nowrap">Rate</TableHead>
                    <TableHead className="whitespace-nowrap">Pmt</TableHead>
                    <TableHead className="whitespace-nowrap">Term</TableHead>
                    <TableHead className="whitespace-nowrap">Balloon</TableHead>
                    <TableHead className="whitespace-nowrap">MD Results</TableHead>
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((r, i) => (
                    <TableRow key={i} className={`text-xs ${r.status === "bad" ? "opacity-40" : ""}`}>
                      <TableCell className="sticky left-0 bg-background z-10"><StatusIcon s={r.status} /></TableCell>
                      <TableCell className="font-mono whitespace-nowrap">{r.document_number}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.county}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.filing_date}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.document_type}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.origin_state}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.first_name}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.last_name}</TableCell>
                      <TableCell className="whitespace-nowrap max-w-[180px] truncate">{r.street_address}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.city}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.state}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.zip_code}</TableCell>
                      <TableCell className="whitespace-nowrap max-w-[150px] truncate">{r.buyer}</TableCell>
                      <TableCell className="whitespace-nowrap max-w-[200px] truncate">{r.property_address}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.loan_amount ? `$${r.loan_amount.toLocaleString()}` : ""}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.interest_rate ? `${r.interest_rate}%` : ""}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.monthly_payment ? `$${r.monthly_payment.toLocaleString()}` : ""}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.term_months || ""}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.balloon_amount ? `$${r.balloon_amount.toLocaleString()}` : ""}</TableCell>
                      <TableCell className="font-mono whitespace-nowrap max-w-[200px] truncate">{r.md_results}</TableCell>
                      <TableCell>
                        <Badge
                          variant={r.status === "good" ? "default" : r.status === "review" ? "secondary" : "destructive"}
                          className="text-[10px] whitespace-nowrap"
                        >
                          {r.statusReason}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* STEP 4: Done */}
        {step === "done" && importResult && (
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg border">
            <CheckCircle className="h-5 w-5 text-primary" />
            <div>
              <p className="font-medium">Melissa Import Complete</p>
              <p className="text-sm text-muted-foreground">
                {importResult.updated} records imported (verified + review).
                {importResult.skipped > 0 && ` ${importResult.skipped} bad addresses skipped.`}
              </p>
            </div>
            <Button variant="outline" size="sm" className="ml-auto" onClick={resetAll}>
              Import Another
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
